/**
 * Performance Debug Utility
 * Включается через ?perf=1 в URL
 * Логирует время mount страниц, TTI, предупреждения о тяжелых операциях
 */

// Проверяем, включен ли perf режим
export const isPerfMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('perf') === '1';
};

// Цвета для консоли
const COLORS = {
  info: 'color: #4fc3f7; font-weight: bold;',
  warn: 'color: #ffb74d; font-weight: bold;',
  error: 'color: #ef5350; font-weight: bold;',
  success: 'color: #81c784; font-weight: bold;',
  time: 'color: #ce93d8; font-weight: bold;',
};

// Логирование с префиксом
const log = (type: keyof typeof COLORS, message: string, ...args: unknown[]) => {
  if (!isPerfMode()) return;
  console.log(`%c[PERF] ${message}`, COLORS[type], ...args);
};

// Замер времени mount компонента
export const measureMount = (componentName: string): (() => void) => {
  if (!isPerfMode()) return () => {};
  
  const startTime = performance.now();
  log('info', `⏱️ ${componentName} mounting...`);
  
  return () => {
    const duration = performance.now() - startTime;
    const color = duration > 100 ? 'warn' : 'success';
    log(color, `✅ ${componentName} mounted in ${duration.toFixed(2)}ms`);
    
    if (duration > 200) {
      log('error', `🐢 SLOW MOUNT: ${componentName} took ${duration.toFixed(0)}ms (>200ms threshold)`);
    }
  };
};

// Замер TTI (Time to Interactive) после навигации
export const measureTTI = (pageName: string): void => {
  if (!isPerfMode()) return;
  
  const startTime = performance.now();
  
  // Ждём следующий idle callback или fallback через requestAnimationFrame
  const measureEnd = () => {
    const tti = performance.now() - startTime;
    const color = tti > 300 ? 'warn' : 'success';
    log(color, `🎯 TTI for ${pageName}: ${tti.toFixed(2)}ms`);
    
    if (tti > 500) {
      log('error', `🐌 SLOW TTI: ${pageName} took ${tti.toFixed(0)}ms to become interactive`);
    }
  };
  
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(measureEnd, { timeout: 2000 });
  } else {
    requestAnimationFrame(() => {
      requestAnimationFrame(measureEnd);
    });
  }
};

// Предупреждение о большом батче карточек
export const warnLargeBatch = (componentName: string, itemCount: number, threshold = 10): void => {
  if (!isPerfMode()) return;
  
  if (itemCount > threshold) {
    log('warn', `⚠️ LARGE BATCH: ${componentName} rendering ${itemCount} items at once (threshold: ${threshold})`);
    log('info', `💡 Consider using batch rendering or virtualization`);
  }
};

// Проверка изображений на lazy loading и размеры
export const checkImageOptimization = (containerSelector: string): void => {
  if (!isPerfMode()) return;
  
  // Откладываем проверку до следующего idle
  const check = () => {
    const container = document.querySelector(containerSelector);
    if (!container) return;
    
    const images = container.querySelectorAll('img');
    let issues = 0;
    
    images.forEach((img, index) => {
      const warnings: string[] = [];
      
      if (!img.hasAttribute('loading') || img.getAttribute('loading') !== 'lazy') {
        warnings.push('missing loading="lazy"');
      }
      
      if (!img.hasAttribute('decoding') || img.getAttribute('decoding') !== 'async') {
        warnings.push('missing decoding="async"');
      }
      
      if (!img.hasAttribute('width') && !img.hasAttribute('height')) {
        const style = window.getComputedStyle(img);
        const hasFixedSize = style.width !== 'auto' && style.height !== 'auto';
        if (!hasFixedSize) {
          warnings.push('no fixed dimensions (causes layout shift)');
        }
      }
      
      if (warnings.length > 0) {
        issues++;
        log('warn', `🖼️ Image #${index + 1} issues: ${warnings.join(', ')}`, img.src?.slice(0, 50));
      }
    });
    
    if (issues === 0 && images.length > 0) {
      log('success', `✅ All ${images.length} images in ${containerSelector} are optimized`);
    } else if (issues > 0) {
      log('error', `🖼️ ${issues}/${images.length} images have optimization issues`);
    }
  };
  
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(check, { timeout: 3000 });
  } else {
    setTimeout(check, 1000);
  }
};

// Логирование навигации
export const logNavigation = (from: string, to: string): void => {
  if (!isPerfMode()) return;
  log('info', `🧭 Navigation: ${from} → ${to}`);
};

// Хук для использования в компонентах
export const usePerfMount = (componentName: string): void => {
  if (!isPerfMode()) return;
  
  // Используем глобальный объект для хранения времени начала
  const key = `__perf_mount_${componentName}`;
  if (!(window as any)[key]) {
    (window as any)[key] = performance.now();
  }
};

// Финализация замера mount (вызывать в useEffect)
export const finishPerfMount = (componentName: string): void => {
  if (!isPerfMode()) return;
  
  const key = `__perf_mount_${componentName}`;
  const startTime = (window as any)[key];
  if (startTime) {
    const duration = performance.now() - startTime;
    delete (window as any)[key];
    
    const color = duration > 100 ? 'warn' : 'success';
    log(color, `✅ ${componentName} mounted in ${duration.toFixed(2)}ms`);
    
    if (duration > 200) {
      log('error', `🐢 SLOW MOUNT: ${componentName} took ${duration.toFixed(0)}ms`);
    }
  }
};

// Инициализация perf режима
export const initPerfMode = (): void => {
  if (!isPerfMode()) return;
  
  console.log('%c🔧 PERF DEBUG MODE ENABLED', 'color: #d4af37; font-size: 16px; font-weight: bold;');
  console.log('%cAdd ?perf=1 to URL to enable performance logging', 'color: #888;');
  console.log('%c─────────────────────────────────────', 'color: #444;');
  
  // Логируем базовую информацию об устройстве
  const nav = navigator as any;
  log('info', `📱 Device info:`, {
    cores: nav.hardwareConcurrency || 'unknown',
    memory: nav.deviceMemory ? `${nav.deviceMemory}GB` : 'unknown',
    connection: nav.connection?.effectiveType || 'unknown',
    isTelegram: !!(window as any).Telegram?.WebApp,
  });
};

// Debug scroll container - для диагностики проблем со скроллом в Telegram WebView
export const debugScrollContainer = (containerSelector: string, activeTab: string): void => {
  if (!isPerfMode()) return;
  
  const container = document.querySelector(containerSelector) as HTMLElement;
  if (!container) {
    log('error', `❌ Scroll container not found: ${containerSelector}`);
    return;
  }
  
  const info = {
    selector: containerSelector,
    activeTab,
    clientHeight: container.clientHeight,
    scrollHeight: container.scrollHeight,
    scrollTop: container.scrollTop,
    canScroll: container.scrollHeight > container.clientHeight,
    overflow: window.getComputedStyle(container).overflow,
    overflowY: window.getComputedStyle(container).overflowY,
    touchAction: window.getComputedStyle(container).touchAction,
  };
  
  log('info', `📜 Scroll debug:`, info);
  
  if (!info.canScroll) {
    log('warn', `⚠️ Content fits in viewport - no scroll needed`);
  }
};

